import type React from 'react';

import { TextField } from '@mui/material';
import { OrdSortableList } from '@ragu-components/ord-components';

import { flowchartLogger } from 'src/lib/logger';

import type { SortableInput, InputColor } from '../dataProvider/types';

interface SortableInputsListProps {
  inputs: SortableInput[];
  inputColors?: Record<number, InputColor>; // map of input index to color validation
  inputLabel: string;
  onChange: React.Dispatch<React.SetStateAction<SortableInput[]>>;
  onDelete: (deletedId: string) => void;
}

const SortableInputsList = ({
  inputs,
  inputColors = {},
  inputLabel,
  onChange,
  onDelete,
}: SortableInputsListProps): React.ReactElement => {
  const onUpdateInputs = (index: number, id: string, value: string): void => {
    const updatedInputs = [...inputs];
    updatedInputs[index] = { id, value };
    onChange(updatedInputs);
  };

  const getColorByIndex = (index: number): InputColor => {
    if (inputColors !== undefined && index in inputColors) {
      const inputColor = inputColors[index];
      if (inputColor !== undefined) {
        flowchartLogger.debug('Getting color for input', index, inputColor);
        return inputColor;
      }
    }
    flowchartLogger.debug('Getting default color for input', index);
    return 'primary';
  };

  return (
    <OrdSortableList
      inputs={inputs as Array<{ id: string; value: string }>}
      onChange={(newInputs) => {
        onChange(newInputs as unknown as SortableInput[]);
      }}
      onRemove={(input) => {
        onDelete(input.id);
      }}
      renderInput={(input, index) => (
        <TextField
          placeholder={`${inputLabel} ${index + 1}`}
          color={getColorByIndex(index)}
          focused={getColorByIndex(index) !== 'primary'}
          onChange={(event) => {
            onUpdateInputs(index, input.id, event.target.value);
          }}
          variant='standard'
          value={input.value}
        />
      )}
    />
  );
};

export default SortableInputsList;
